import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "@/components/ui/use-toast";
import axiosInstance from "../../utils/axiosInstance";

// Fetch Membership Plans
export const fetchMembershipPlans = createAsyncThunk(
  "parts/fetchMembershipPlans",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/plans");
      const { data } = response.data;

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "Error fetching plans";
      console.error("Fetch plans error:", error);
      toast({
        title: "Fetching Error",
        description: message,
        variant: "destructive",
      });
      return rejectWithValue(message);
    }
  },
);

// Fetch Membership AddOns
export const fetchMembershipAddOns = createAsyncThunk(
  "parts/fetchMembershipAddOns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/plans?addon=true");
      const { data } = response.data;

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "Error fetching add-ons";
      console.error("Fetch add-ons error:", error);
      toast({
        title: "Fetching Error",
        description: message,
        variant: "destructive",
      });
      return rejectWithValue(message);
    }
  },
);

// Create Subscription
export const createSubscription = createAsyncThunk(
  "parts/createSubscription",
  async ({ payload, onSuccess = () => {} }, { rejectWithValue }) => {
    try {
      const { userId, planId, paymentMethodId, isNewSeller, isTrial } = payload;
      const finalPayload = {
        userId,
        planId,
        ...(isNewSeller && { isNewSeller }),
        ...(isTrial && { isTrial: true }),
      };
      if (planId !== "free" && paymentMethodId) {
        finalPayload.paymentMethodId = paymentMethodId;
      }

      const response = await axiosInstance.post("/subscriptions", finalPayload);
      const { data } = response.data;

      onSuccess?.();

      toast({
        title: "Subscription Created",
        description: `Your ${planId} plan has been activated.`,
        variant: "success",
      });

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error activating your plan after payment. Please contact support.";

      console.error("Create subscription error:", error);

      toast({
        title: "Activation Failed",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  },
);
// Update Subscription
export const updateSubscription = createAsyncThunk(
  "parts/updateSubscription",
  async ({ payload, onSuccess = () => {} }, { rejectWithValue }) => {
    console.log("payload: ", payload);
    try {
      const {
        subscriptionId,
        newPriceId,
        paymentMethodId,
        isDowngrade,
        isNewSeller,
      } = payload;

      // build payload
      const finalPayload = {
        subscriptionId,
        ...(isDowngrade && { isDowngrade }),
        ...(isNewSeller && { isNewSeller }),
        newPriceId,
        ...(paymentMethodId && { paymentMethodId }),
      };

      const response = await axiosInstance.put(
        "/subscriptions/modify",
        finalPayload,
      );
      const { data } = response.data;

      onSuccess?.();

      toast({
        title: "Subscription Updated",
        description: `Your plan has been changed activated.`,
        variant: "success",
      });

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error updating your subscription. Please contact support.";

      console.error("Create subscription error:", error);

      toast({
        title: "Update Failed",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  },
);
// Add Or Remove Add on from subscription
export const addOrRemoveAddOnFromSubscription = createAsyncThunk(
  "parts/addOrRemoveAddOnFromSubscription",
  async ({ payload, onSuccess = () => {} }, { rejectWithValue }) => {
    try {
      const { subscriptionId, addonPriceId, paymentMethodId, action } = payload;
      // build payload
      const finalPayload = {
        subscriptionId,
        addonPriceId,
        ...(paymentMethodId && { paymentMethodId }),
        action: action === "add" ? "add_addon" : "remove_addon",
      };

      const response = await axiosInstance.put(
        "/subscriptions/modify",
        finalPayload,
      );
      const { data } = response.data;

      onSuccess?.();

      toast({
        title: "Subscription Updated",
        description:
          action === "add"
            ? "The add-on has been successfully added to your subscription."
            : "The add-on has been successfully removed from your subscription.",
        variant: "success",
      });

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error updating your subscription. Please contact support.";

      console.error("Add/Remove add-on error:", error);

      toast({
        title: "Update Failed",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  },
);

// Acknowledge Trial End
export const acknowledgeTrialEnd = createAsyncThunk(
  "membership/acknowledgeTrialEnd",
  async ({ userId, onSuccess = () => {} }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        "/subscriptions/acknowledge-trial-end",
        { userId },
      );
      const { data } = response.data;

      onSuccess?.();

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to acknowledge trial end. Please try again.";

      console.error("Acknowledge trial end error:", error);

      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  },
);

// Create Featured Part
export const createFeaturedPart = createAsyncThunk(
  "parts/createFeaturedPart",
  async ({ payload, onSuccess = () => {} }, { rejectWithValue }) => {
    try {
      const { userId, partId, weeks, paymentMethodId } = payload;

      const response = await axiosInstance.post(
        "/subscriptions/createFeaturedProduct",
        payload,
      );
      const { data } = response.data;

      onSuccess?.();

      toast({
        title: "Payment Successful!",
        description: `Your part has been featured for ${weeks} week${
          weeks > 1 ? "s" : ""
        }.`,
        variant: "success",
      });

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error activating your featured part. Please contact support.";

      console.error("Create featured product error:", error);

      toast({
        title: "Featured Part Failed",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  },
);
